import { Doctor, TimeSlot } from "../types/doctor.definition";
import {
  assignDoctorToFacility,
  createDoctor,
  createDoctorTimeSlots,
  deleteDoctorTimeSlot,
  deleteTimeSlotsByDateRange,
  getDoctorAvailableTimeSlots,
  getDoctorAvailableTimeSlotsByDate,
  getDoctorAvailableTimeSlotsByDateRange,
  getDoctorById,
  getDoctorTimeSlots,
  getDoctorTimeSlotsByDate,
  getDoctorTimeSlotsByDateRange,
  unassignDoctorFromFacility,
  updateDoctor,
} from "../webclients/doctor.client";
import { resendTemporaryPassword } from "../webclients/manager.client";

export const doctorApi = {
  getDoctorById: async (doctorId: string) => {
    const response = await getDoctorById(doctorId);
    return response.data;
  },
  createDoctor: async (facilityId: string | number, data: Doctor) => {
    const response = await createDoctor(facilityId, data);
    return response.data;
  },
  updateDoctor: async (doctorId: string, data: Partial<Doctor>) => {
    const response = await updateDoctor(doctorId, data);
    return response.data;
  },
  // Get all time slots (includes all statuses: available, booked, cancelled, expired)
  getDoctorTimeSlots: async (doctorId: string): Promise<TimeSlot[]> => {
    const response = await getDoctorTimeSlots(doctorId);
    return response.data;
  },
  // Get only available time slots
  getDoctorAvailableTimeSlots: async (
    doctorId: string
  ): Promise<TimeSlot[]> => {
    const response = await getDoctorAvailableTimeSlots(doctorId);
    return response.data;
  },
  // Get all time slots by date
  getDoctorTimeSlotsByDate: async (
    doctorId: string,
    date: string
  ): Promise<TimeSlot[]> => {
    const response = await getDoctorTimeSlotsByDate(doctorId, date);
    return response.data;
  },
  // Get available time slots by date
  getDoctorAvailableTimeSlotsByDate: async (
    doctorId: string,
    date: string
  ): Promise<TimeSlot[]> => {
    const response = await getDoctorAvailableTimeSlotsByDate(doctorId, date);
    return response.data;
  },
  // Get all time slots by date range
  getDoctorTimeSlotsByDateRange: async (
    doctorId: string,
    startDate: string,
    endDate: string
  ): Promise<TimeSlot[]> => {
    const response = await getDoctorTimeSlotsByDateRange(
      doctorId,
      startDate,
      endDate
    );
    return response.data;
  },
  // Get available time slots by date range
  getDoctorAvailableTimeSlotsByDateRange: async (
    doctorId: string,
    startDate: string,
    endDate: string
  ): Promise<TimeSlot[]> => {
    const response = await getDoctorAvailableTimeSlotsByDateRange(
      doctorId,
      startDate,
      endDate
    );
    return response.data;
  },
  // Create time slots
  createDoctorTimeSlots: async (
    doctorId: string,
    data: {
      id?: number;
      daysOfWeek: string[];
      startTime: string;
      endTime: string;
      isRecurring: boolean;
      slotDurationMinutes?: number;
      startDate: string;
      endDate: string;
    }
  ): Promise<TimeSlot[]> => {
    const response = await createDoctorTimeSlots(doctorId, data);
    return response.data;
  },
  // Delete time slot
  deleteDoctorTimeSlot: async (
    doctorId: string,
    timeSlotId: number
  ): Promise<void> => {
    await deleteDoctorTimeSlot(doctorId, timeSlotId);
  },
  // Delete available time slots by date range; returns booked slots that could not be deleted
  deleteDoctorTimeSlotsByDateRange: async (
    doctorId: string,
    startDate: string,
    endDate: string
  ): Promise<TimeSlot[]> => {
    const response = await deleteTimeSlotsByDateRange(
      doctorId,
      startDate,
      endDate
    );
    return response.data;
  },
  // Resend temporary password
  resendTemporaryPassword: async (userId: string): Promise<void> => {
    await resendTemporaryPassword(userId);
  },
  // Remove a doctor from a facility (unassign, does not delete the doctor account)
  unassignFromFacility: async (
    facilityId: string | number,
    doctorId: string | number
  ): Promise<void> => {
    await unassignDoctorFromFacility(facilityId, doctorId);
  },
  // Assign an existing doctor to a facility without creating a new account
  assignToFacility: async (
    facilityId: string | number,
    doctorId: string | number
  ): Promise<void> => {
    await assignDoctorToFacility(doctorId, facilityId);
  },
};

export default doctorApi;
